import { AutocompleteRenderInputParams, Box } from '@mui/material';
import { formatTranslation, unsafeTranslationKey } from 'intl';
import { Disabled } from 'components/dataCreationForms/withInputForm/statefulInputsWrapper/types';
import { WithCreateCompany } from 'components/dataCreationForms/company/withCreateCompany';
import { WithCreateLevel } from 'components/dataCreationForms/level/withCreateLevel';
import { WithCreateUser } from 'components/dataCreationForms/user/withCreateUser';
import { ErrorPresentation } from 'helpers/validators';
import React, {
  PropsWithChildren,
  ReactElement,
  Ref,
  forwardRef,
  memo,
  useCallback,
  useMemo,
} from 'react';
import { useIntl } from 'react-intl';
import { compareObjectIds } from 'shared/utils/id/compare';
import { EditableStandardEntityName } from 'shared/domain/fieldValue/fields';
import { LabelledEntity } from 'shared/types/commonView';
import { FieldSkeleton } from 'shared/types/form';
import {
  getFreeSoloProps,
  getAutocompleteDisplayedHelperText as getSelectDisplayedHelperText,
  PopupControlStrategy,
  unifiedGetOptionLabel,
} from '../common';
import { MemoMultiAutocomplete } from './multiAutocomplete';
import { MemoMultiSelect } from './multiSelect';
import { FormValuesGetter } from '../types';
import { MemoCreateLevelWizardDialog } from 'views/level/wizard/create/dialog';
import { MemoCreateUserWizardDialog } from 'views/users/wizard/create/dialog';
import { MemoCreateCompanyWizardDialog } from 'views/companies/wizard/create/dialog';

// These wizard dialogs are rendered inline (not hoisted to module-scope
// constants): their Form -> statefulInputsWrapper chain imports
// MemoMultiChoice from this very file, so evaluating <XxxWizardDialog />
// at module scope can hit it mid-initialization (TDZ) depending on which
// module happens to start the load cycle first. Deferring to render time
// (after the whole module graph has loaded) avoids that regardless of
// import order.

function WithCreateLevelForMultiselect({
  children,
  value,
  handleChange,
}: PropsWithChildren<{
  value: LabelledEntity[] | null;
  handleChange: (_: unknown, value: LabelledEntity[]) => void;
}>): ReactElement {
  const valueRef = React.useRef(value);
  valueRef.current = value;

  const onSuccessCallback = useCallback(
    (_id: string, label: string) => {
      const current = valueRef.current || [];
      if (current.some((item) => compareObjectIds(item._id, _id, 'WithCreateLevelForMultiselect'))) {
        return;
      }
      const newEntity: LabelledEntity = { _id, label };
      handleChange(null, [...current, newEntity]);
    },
    [handleChange]
  );

  return (
    <WithCreateLevel onSuccessCallback={onSuccessCallback}>
      {children as ReactElement}
    </WithCreateLevel>
  );
}

function WithCreateUserForMultiselect({
  children,
  value,
  handleChange,
}: PropsWithChildren<{
  value: LabelledEntity[] | null;
  handleChange: (_: unknown, value: LabelledEntity[]) => void;
}>): ReactElement {
  const valueRef = React.useRef(value);
  valueRef.current = value;

  const onSuccessCallback = useCallback(
    (_id: string, label: string) => {
      const current = valueRef.current || [];
      if (
        current.some((item) =>
          compareObjectIds(item._id, _id, 'WithCreateUserForMultiselect')
        )
      ) {
        return;
      }
      const newEntity: LabelledEntity = { _id, label };
      handleChange(null, [...current, newEntity]);
    },
    [handleChange]
  );

  return (
    <WithCreateUser onSuccessCallback={onSuccessCallback}>
      {children as ReactElement}
    </WithCreateUser>
  );
}

function WithCreateCompanyForMultiselect({
  children,
  value,
  handleChange,
}: PropsWithChildren<{
  value: LabelledEntity[] | null;
  handleChange: (_: unknown, value: LabelledEntity[]) => void;
}>): ReactElement {
  const valueRef = React.useRef(value);
  valueRef.current = value;

  const onSuccessCallback = useCallback(
    (_id: string, label: string) => {
      const current = valueRef.current || [];
      if (
        current.some((item) =>
          compareObjectIds(item._id, _id, 'WithCreateCompanyForMultiselect')
        )
      ) {
        return;
      }

      const newEntity: LabelledEntity = { _id, label };
      handleChange(null, [...current, newEntity]);
    },
    [handleChange]
  );

  return (
    <WithCreateCompany onSuccessCallback={onSuccessCallback}>
      {children as ReactElement}
    </WithCreateCompany>
  );
}

interface MultiChoiceProps {
  available: LabelledEntity[];
  groupBy?: (option: LabelledEntity) => string;
  defaultValue: LabelledEntity[];
  labelId: string;
  localisedLabel: string;
  required: boolean;
  disabled?: Disabled;
  handleChange: (_: any, value: LabelledEntity[]) => void;
  error?: string;
  value: LabelledEntity[] | null;
  id: string;
  disableClearable?: boolean;
  hidden?: boolean;
  field?: FieldSkeleton;
  renderInput?: (
    params: AutocompleteRenderInputParams
  ) => React.ReactElement;
  renderOption?: (
    props: object,
    option: LabelledEntity
  ) => React.ReactElement;
  entityName?: string;
  helperText?: string;
  reserveSpaceForHelperText: boolean;
  dense: boolean;
  formValuesGetter?: FormValuesGetter;
  popupControlStrategy?: PopupControlStrategy;
  maxVisibleTags?: number;
}

const MultiChoice = (
  {
    available,
    groupBy,
    value,
    handleChange,
    disabled,
    id,
    required,
    labelId,
    localisedLabel,
    error,
    disableClearable,
    entityName,
    helperText,
    dense,
    reserveSpaceForHelperText,
    formValuesGetter,
    popupControlStrategy,
    maxVisibleTags,
  }: MultiChoiceProps,
  ref: Ref<HTMLDivElement>
): React.ReactElement => {
  const intl = useIntl();
  const multiChoiceProps = useMemo(
    () => ({
      available,
      groupBy,
      disabled: disabled,
      value: value || null,
      id: `${id || labelId}-multiChoice`,
      localisedLabel: `${
        localisedLabel || (labelId && formatTranslation(intl, unsafeTranslationKey(labelId) ))
      }`,
      error,
      ref,
      required: !!required,
      handleChange,
      helperText: getSelectDisplayedHelperText(intl, helperText, error),
      getOptionLabel: unifiedGetOptionLabel,
      disableClearable: !!disableClearable,
      dense,
      reserveSpaceForHelperText,
      popupControlStrategy,
      ...(maxVisibleTags !== undefined ? { maxVisibleTags } : {}),
    }),
    [
      available,
      groupBy,
      disabled,
      value,
      id,
      error,
      ref,
      required,
      handleChange,
      disableClearable,
      dense,
      reserveSpaceForHelperText,
      labelId,
      intl,
      helperText,
      localisedLabel,
      popupControlStrategy,
      maxVisibleTags,
    ]
  );

  if (entityName) {
    const freeSoloProps = {
      entityName,
      formValuesGetter,
      ...getFreeSoloProps(entityName),
    };
    if (freeSoloProps?.Provider) {
      if (entityName === EditableStandardEntityName.level) {
        return (
          <WithCreateLevelForMultiselect
            value={value}
            handleChange={handleChange}
          >
            <>
              <MemoMultiAutocomplete
                {...multiChoiceProps}
                {...freeSoloProps}
                DialogComponent={undefined}
              />
              <MemoCreateLevelWizardDialog />
            </>
          </WithCreateLevelForMultiselect>
        );
      }
      if (entityName === EditableStandardEntityName.user) {
        return (
          <WithCreateUserForMultiselect
            value={value}
            handleChange={handleChange}
          >
            <>
              <MemoMultiAutocomplete
                {...multiChoiceProps}
                {...freeSoloProps}
                DialogComponent={undefined}
              />
              <MemoCreateUserWizardDialog />
            </>
          </WithCreateUserForMultiselect>
        );
      }
      if (entityName === EditableStandardEntityName.company) {
        return (
          <WithCreateCompanyForMultiselect
            value={value}
            handleChange={handleChange}
          >
            <>
              <MemoMultiAutocomplete
                {...multiChoiceProps}
                {...freeSoloProps}
                DialogComponent={undefined}
              />
              <MemoCreateCompanyWizardDialog />
            </>
          </WithCreateCompanyForMultiselect>
        );
      }
      return (
        <freeSoloProps.Provider>
          <MemoMultiAutocomplete
            {...multiChoiceProps}
            {...freeSoloProps}
          />
        </freeSoloProps.Provider>
      );
    }
  }

  if (available.length > 5) {
    return <MemoMultiAutocomplete {...multiChoiceProps} />;
  }
  return (
    <MemoMultiSelect
      {...multiChoiceProps}
      labelId={labelId}
      id={`${multiChoiceProps.id}-select`}
    />
  );
};

const ForwardedMultiChoice = forwardRef<HTMLDivElement, MultiChoiceProps>(
  MultiChoice
) as React.ForwardRefExoticComponent<MultiChoiceProps>;

export const MemoMultiChoice = memo(ForwardedMultiChoice);
